<template>
  <div class="telemedicine-video">
    <div
      v-show="isLoading"
      class="telemedicine__waiting telemedicine-video--full-height"
    >
      <span
        :class="{ '!text-sm !p-0': viewport.name === 'mobile' }"
      >
        {{ msg }}
      </span>

      <dr-loader
        v-show="isLoading"
        size="lg"
        class="telemedicine__loader"
      />
    </div>

    <div id="remote-media" />
    <div id="local-media" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { connect } from 'twilio-video'
import EventBus from '@/globals/eventBus'

export default {
  name: 'TelemedicineVideo',

  props: {
    token: {
      type: String,
      required: true,
      default: () => ''
    }
  },

  data () {
    return {
      room: null,
      localTrack: false,
      previewTracks: null,
      broadcasting: false,
      hasWebcam: false,
      isLoading: true,
      msg: 'Seu paciente foi notificado dessa consulta por e-mail e SMS. Aguardando a conexão...'
    }
  },

  computed: {
    ...mapGetters({ viewport: 'getViewport' })
  },

  created () {
    this.createChat('nome-da-sala')

    EventBus.$on(
      'telemedicine-disconnect',
      () => this.disconnectFromRoom()
    )

    EventBus.$on(
      'telemedicine-toggle-audio',
      payload => this.toggleAudio(payload)
    )

    EventBus.$on(
      'telemedicine-toggle-video',
      payload => this.toggleVideo(payload)
    )

    window.addEventListener('beforeunload', this.leaveRoomIfJoined)
    window.addEventListener('pagehide', this.leaveRoomIfJoined)
  },

  mounted () {
    const remoteMedia = document.querySelector('#remote-media')

    const observer = new MutationObserver(() => {
      this.removeExtraVideoTagsFromContainer()
    })

    observer.observe(remoteMedia, {
      childList: true,
      subtree: true
    })
  },

  methods: {
    resizeRemoteVideo () {
      this.$nextTick(() => {
        const wrapper = document.querySelector('#remote-media')
        wrapper.classList.remove('remote-video-portrait')

        const video = wrapper.querySelector('video')
        if (video) {
          const width = video.offsetWidth
          const height = video.offsetHeight

          if (height > width) { // portrait
            wrapper.classList.add('remote-video-portrait')
          }
        }
      })
    },

    removeExtraVideoTagsFromContainer () {
      const wrapper = document.querySelector('#remote-media')
      const videos = wrapper.querySelectorAll('video')
      if (videos.length > 1) {
        videos[0].parentNode.removeChild(videos[0])
      }
    },

    leaveRoomIfJoined () {
      if (this.room) {
        this.room.disconnect()
      }
    },

    disconnectFromRoom () {
      if (this.room) {
        this.room.disconnect()
      }

      this.$store.dispatch('setTelemedicineBroadcasting', false)
      EventBus.$emit('telemedicine-destroyed')
    },

    toggleAudio (status) {
      this.room.localParticipant.audioTracks.forEach((publication) => {
        if (status) {
          publication.track.disable()
        } else {
          publication.track.enable()
        }
      })
    },

    toggleVideo (status) {
      this.room.localParticipant.videoTracks.forEach((publication) => {
        if (status) {
          publication.track.disable()
        } else {
          publication.track.enable()
        }
      })
    },

    attachTracks (tracks, container) {
      tracks.forEach((track) => {
        container.appendChild(track.attach())
        this.resizeRemoteVideo()
      })
    },

    detachTracks (tracks) {
      tracks.forEach((track) => {
        track.detach()
          .forEach((detachedElement) => detachedElement.remove())
      })
    },

    trackPublished (publication, container) {
      publication.on(
        'subscribed',
        (track) => {
          this.attachTracks([track], container)

          if (/video/gi.test(publication.kind)) {
            publication.on('dimensionsChanged', this.resizeRemoteVideo)
          }
        }
      )

      if (publication.track) {
        this.attachTracks([publication.track], container)
      }
    },

    participantConnected (participant, container) {
      participant.tracks
        .forEach(publication => this.trackPublished(publication, container))

      participant.on(
        'trackPublished',
        publication => this.trackPublished(publication, container)
      )

      participant.on('trackUnpublished', () => {})

      this.isLoading = false
      this.$nextTick(() => {
        const remoteMedia = document.querySelector('#remote-media')
        remoteMedia.style.display = 'flex'
        remoteMedia.style.alignItems = 'center'
        remoteMedia.style.justifyContent = 'space-around'
      })
    },

    getTracks (participant) {
      return Array.from(participant.tracks.values())
        .filter(publication => publication.track)
        .map(publication => publication.track)
    },

    detachParticipantTracks (participant) {
      const tracks = this.getTracks(participant)
      this.detachTracks(tracks)
    },

    createChat (roomName) {
      navigator.mediaDevices.enumerateDevices()
        .then((devices) => {
          this.hasWebcam = devices.some(d => /video/gi.test(d.kind))
          if (!this.hasWebcam) {
            alert('Você precisa ter uma câmera em seu dispositivo para iniciar uma consulta do tipo telemedicina.')
            EventBus.$emit('telemedicine-destroyed')
            return false
          }
          // return this.previewTracks
          //   ? this.previewTracks
          //   : this.hasWebcam
          //     ? createLocalTracks({ audio: true, video: true })
          //     : createLocalAudioTrack()
        })
        .then((tracks) => {
          console.log(tracks)
          const connectOptions = {
            automaticSubscription: true,
            name: roomName,
            audio: true,
            video: this.hasWebcam
            // tracks: Array.isArray(tracks) ? tracks : [tracks]
          }

          return connect(this.token, connectOptions)
        })
        .then(room => this.joinRoom(room))
        .catch((err) => {
          if ((err.name === 'NotAllowedError') ||
            (err.name === 'PermissionDismissedError')) {
            alert('Você precisa autorizar a câmera e o microfone em seu dispositivo para iniciar uma consulta do tipo telemedicina.')
            EventBus.$emit('telemedicine-destroyed')
          }
        })
    },

    joinRoom (room) {
      this.room = room
      const previewContainer = document.querySelector('#local-media') || null

      if (!previewContainer.querySelector('video')) {
        this.attachTracks(this.getTracks(this.room.localParticipant), previewContainer)
      }

      const remoteMediaContainer = document.querySelector('#remote-media')
      room.participants
        .forEach(participant => {
          this.participantConnected(participant, remoteMediaContainer)
        })

      room.on(
        'participantConnected',
        (participant) => {
          this.participantConnected(participant, remoteMediaContainer)

          participant.on(
            'trackSubscribed',
            (remoteTrack) => {
              if (/video/gi.test(remoteTrack.kind)) {
                remoteTrack.on('dimensionsChanged', this.resizeRemoteVideo)
              }
            }
          )
        }
      )

      room.on(
        'participantDisconnected',
        (participant) => {
          this.msg = 'O paciente se desconectou. Aguarde ou termine a consulta através dos controles abaixo.'
          this.isLoading = true
          this.$nextTick(() => (document.querySelector('#remote-media').style.display = 'none'))
          this.detachParticipantTracks(participant)
        }
      )

      room.on(
        'disconnected',
        () => {
          if (this.previewTracks) {
            this.previewTracks.forEach(track => track.stop())
          }

          this.previewTracks = null

          this.detachParticipantTracks(this.room.localParticipant)
          this.room.participants.forEach(this.detachParticipantTracks)
          this.room = null
        }
      )

      EventBus.$emit('telemedicine-started')
      this.$store.dispatch('setTelemedicineBroadcasting', true)
    }
  }
}
</script>
